import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { viewModelToBulkModelForConversionGoals } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { CampaignSubTypeMapping, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { ERROR_ENTITY, isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors, isDealCampaign } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { bulkModelToViewModelForCampaign, viewModelToModelForCampaign } from './campaign';
import { viewModelToBulkModelForDealTargets, viewModelToBulkModelForLocationTargets, viewModelToBulkModelForDeviceTargets } from './campaign-criterions';

const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
const campaignResource = new campaignEDM.ResourceIdentifier();

const createOrUpdateCampaign = (data) => {
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const bulkUpsertCampaignSettingsEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
};

const allSettledPromiseWrapper = (promises) => {
  const wrappedPromises = promises.map(p => Promise.resolve(p)
    .then(
      val => ({ status: 'fulfilled', value: val }),
      err => ({ status: 'rejected', reason: err })
    ));
  return Promise.all(wrappedPromises);
};

export const getErrorsPlaceholders = () => ({
  systemErrors: [],
  serverErrors: [],
});

export const saveCampaign = ({
  campaign: campaignViewModel,
  viewModelToBulkModelForAdSchedules = null,
  bundleCountries = null,
  bundleLocationCriterions = null,
  viewModelToBulkModelForDisclaimerAssociation = null,
  isAudienceMMACTAOptOutEnabled = false,
  viewModelToBulkModelForVerifiedTrackingSettings = null,
}, { scenario, i18n, activityLogger }) => {
  const campaignModel = viewModelToModelForCampaign(campaignViewModel);

  let conversionGoalsAssociations = {};
  let adExtensionAssociations = {};
  let adSchedulesModel = [];
  let locationTargetsModel = [];
  let verifiedTrackingDataObj = {};

  const modelToSave = {
    ...campaignModel,
    ...conversionGoalsAssociations,
  };

  // Add conversion goals to Native, Display and Video campaigns
  if (campaignViewModel.data.CampaignType === 'Audience' &&
    (campaignViewModel.data.CampaignSubType === CampaignSubTypeMapping.AudienceStatic ||
      campaignViewModel.data.CampaignSubType === CampaignSubTypeMapping.DisplayAds ||
      campaignViewModel.data.CampaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds
    ) && campaignViewModel.data.conversionGoals) {
    conversionGoalsAssociations = {
      CampaignConversionGoalAssociations: viewModelToBulkModelForConversionGoals(campaignViewModel, campaignViewModel.data.Id || -111),
    };
    _.extend(modelToSave, { ...conversionGoalsAssociations });
  }
  // Add ads schedule and location targets for Native campaigns
  if (campaignViewModel.data.CampaignType === 'Audience' && campaignViewModel.data.CampaignSubType === CampaignSubTypeMapping.AudienceStatic) {
    if (viewModelToBulkModelForAdSchedules) {
      adSchedulesModel = viewModelToBulkModelForAdSchedules({ campaignViewModel });
    }

    if (bundleLocationCriterions) {
      locationTargetsModel = viewModelToBulkModelForLocationTargets({
        bundleLocationCriterions,
        campaignViewModel,
      });
    }

    if (viewModelToBulkModelForDisclaimerAssociation) {
      adExtensionAssociations = viewModelToBulkModelForDisclaimerAssociation({ campaignViewModel });
    }

    _.extend(modelToSave, { ...adExtensionAssociations });
    modelToSave.CampaignCriterions = [
      ...adSchedulesModel,
      ...locationTargetsModel,
      ...viewModelToBulkModelForDeviceTargets({ campaignViewModel }),
    ];

    if (isAudienceMMACTAOptOutEnabled) {
      modelToSave.CallToActionOptOut = campaignViewModel.data.IsCallToActionOptOut;
    }
  }

  if (isDealCampaign()) {
    modelToSave.IsDealCampaign = true;
    const dealModel = viewModelToBulkModelForDealTargets(campaignViewModel);
    modelToSave.CampaignCriterions = [dealModel];
  }

  if (campaignViewModel.data.CampaignType === 'Audience' && viewModelToBulkModelForVerifiedTrackingSettings) {
    const campaignData = _.get(campaignViewModel, ['data'], {});
    if (_.has(campaignData, 'VerifiedTrackingSettings')) {
      const verifiedTrackingSettings = _.get(campaignData, ['VerifiedTrackingSettings'], []);
      verifiedTrackingDataObj = viewModelToBulkModelForVerifiedTrackingSettings(verifiedTrackingSettings);
      _.extend(modelToSave, verifiedTrackingDataObj);
    }
  }

  const saveCampaignPromise = createOrUpdateCampaign(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = {
        campaign: bulkModelToViewModelForCampaign({
          campaignModel: result,
          originalCampaignModel: campaignViewModel,
          bundleCountries,
        }),
        ...getErrorsPlaceholders(),
      };

      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;
        const campaign = entity ?
          bulkModelToViewModelForCampaign({
            campaignModel: entity,
            originalCampaignModel: campaignViewModel,
          }) :
          campaignViewModel;

        let filteredErrors = errors;
        if (campaign.data.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertCampaignSettingsEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          campaign,
          serverErrors,
        });
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign: campaignViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
        // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
      });
    });
  let saveExclusionsPromise = Promise.resolve();
  if (campaignViewModel && campaignViewModel.data && campaignViewModel.data.exclusions) {
    const saveExclusionsData = {
      campaignId: campaignViewModel.data.Id,
      AdvanceSettings: {
        Exclusions: {
          ExcludedIPs: campaignViewModel.data.exclusions.campaignExclusions.ExcludedIPs,
          ExcludedSites: campaignViewModel.data.exclusions.campaignExclusions.ExcludedSites,
        },
      },
    };

    saveExclusionsPromise = exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.Campaign);
  }

  const savePromises = [saveCampaignPromise, saveExclusionsPromise];

  return new Promise((resolve, reject) => {
    allSettledPromiseWrapper(savePromises).then((results) => {
      const allSuccess = _.every(results, result => result.status === 'fulfilled');

      if (allSuccess) {
        return resolve({
          campaign: results[0].value.campaign,
          ...getErrorsPlaceholders(),
        });
      }

      const errors = _.map(results, (result) => {
        let { serverErrors = null } = result.reason || {};

        if (result.reason && result.reason.NegativeSitesResult) {
          const { NegativeSitesResult } = result.reason;
          serverErrors = {
            property: 'webExclusions',
            NegativeSitesResult: NegativeSitesResult.EntityErrors,
          };
        }

        if (result.reason && result.reason.NegativeIPsResult) {
          const { NegativeIPsResult } = result.reason;
          serverErrors = {
            property: 'webExclusions',
            NegativeIPsResult: NegativeIPsResult.EntityErrors,
          };
        }

        return serverErrors;
      });

      // eslint-disable-next-line prefer-promise-reject-errors
      return reject({
        campaign: campaignViewModel,
        serverErrors: _.flatten(errors.filter(val => val !== null)),
      });
    });
  });
};
